import { Button, DatePicker, message, Modal } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutSite } from '../components/layout';
import { Loader } from '../components/loader';
import { useLocalStorage } from '../localstorage';
import { get_events_list } from '../services/api.service';
import { formatdateYYYYMMDD, MLEventSaved, MLRace } from '../types';
import './events.scss';
import { LeftCircleFilled, RightCircleFilled } from '@ant-design/icons';

/* -------------------------------------------------------------------------------- *\
|                               MLEventMeeting
\* -------------------------------------------------------------------------------- */
class MLEventMeeting
{
    public meetingdate: string = "";
    public sic: string = "";
    public events: MLEventSaved[] = [];
}
/* -------------------------------------------------------------------------------- *\
|                               DlgContent
\* -------------------------------------------------------------------------------- */
class DlgContent
{
    public racebefore: MLRace | null = null;
    public raceafter: MLRace = new MLRace();
    public context: string = "";
}
/* -------------------------------------------------------------------------------- *\
|                               EventsPage
\* -------------------------------------------------------------------------------- */
export const EventsPage: FC<{ children?: ReactNode }> = ({ children }) =>
{
    const [lastselecteddate, setlastselecteddate] = useLocalStorage('lastdate', formatdateYYYYMMDD(new Date()));
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [currentdate, setCurrentDate] = useState<string>(lastselecteddate);
    const [events, setEvents] = useState<MLEventSaved[]>([]);
    const [daypicker, setDaypicker] = useState<Dayjs>(dayjs(lastselecteddate));
    const [lasttime, setLastTime] = useState<number>(0);
    const [meetings, setMeetings] = useState<MLEventMeeting[]>([]);
    const [dlgvisible, setDlgvisible] = useState(false);
    const [dlgcontent, setDlgContent] = useState<DlgContent>(new DlgContent());

    var g_inrefresh: boolean = false;
    /* -------------------------------------------------------------------------------- *\
    |                               refreshEvents
    \* -------------------------------------------------------------------------------- */
    const refreshEvents = async (currentdate: string) =>
    {
        if (g_inrefresh) return Promise.resolve();

        g_inrefresh = true;
        return get_events_list(currentdate, lasttime).then((newevents: MLEventSaved[]) =>
        {
            newevents = newevents.filter(ne => events.find(e => e.created === ne.created) === undefined);
            if (newevents.length > 0)
            {
                const now: number = new Date().getTime() - (lasttime === 0 ? 30000 : 0);
                for(const e of newevents)
                {
                    e.date = now;
                }

                const ees = [...newevents, ...events];
                setEvents(ees);

                const siclist: string[] = Array.from(new Set(ees.map(e => e.context.substring(8, 11)))).sort((a, b) => a.localeCompare(b));
                setMeetings(siclist.map(sic => ({ meetingdate: currentdate, sic: sic, events: ees.filter(e => e.context.substring(8, 11) === sic) })));

                setLastTime(newevents[0].created);
            }
            else
            {
                const ms = [...meetings];
                setMeetings(ms);
            }
            g_inrefresh = false;
            return Promise.resolve();
        })
        .catch(error =>
        {
            g_inrefresh = false;
            message.error(error.message);
            if (error.status === 403 || error.status === 401) navigate('/login');
            return Promise.resolve();
        });
    }
    /* -------------------------------------------------------------------------------- *\
    |                               changeCurrentDate
    \* -------------------------------------------------------------------------------- */
    const changeCurrentDate = (date: string) =>
    {
        setEvents([]);
        setMeetings([]);
        setLastTime(0);
        setCurrentDate(date);
        setDaypicker(dayjs(date));
        setlastselecteddate(date);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               datepicker_handler
    \* -------------------------------------------------------------------------------- */
    function datepicker_handler(date: any)
    {
        changeCurrentDate(date.format('YYYYMMDD'));
    }
    /* -------------------------------------------------------------------------------- *\
    |                               formattime
    \* -------------------------------------------------------------------------------- */
    function formattime(ts: number)
    {
        let time = new Date(ts);
        return String(Math.floor(time.getHours())).padStart(2, '0') +
        ":" +  String(Math.floor(time.getMinutes())).padStart(2, '0') +
        ":" +  String(Math.floor(time.getSeconds())).padStart(2, '0');
    }
    /* -------------------------------------------------------------------------------- *\
    |                               showContent
    \* -------------------------------------------------------------------------------- */
    function showContent(event: MLEventSaved)
    {
        setDlgContent({context: event.context + " created at " + formattime(event.created) + "", racebefore: event.beforerace, raceafter: event.afterrace});
        setDlgvisible(true);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               effects
    \* -------------------------------------------------------------------------------- */
    useEffect(() => {
        refreshEvents(currentdate).then(() => setLoading(false));
        setLoading(true);
        // eslint-disable-next-line
    }, [currentdate]);

    useEffect(() => {
        const interval = setInterval(() =>
        {
            refreshEvents(currentdate);
        }, 1000);
        return () => clearInterval(interval);

        // eslint-disable-next-line
    }, [currentdate, lasttime, events]);

    useEffect(() => {
    }, [events]);

    const now: number = new Date().getTime();

    return (
        <LayoutSite>
            <div className="events">
                {loading ? <Loader /> : <></>}
                <div className='events__wrapper'>
                    <div className="events__header">
                        <div></div>
                    </div>
                    <div className='events__calendar'>
                        <div className='events__calendar__selector'>
                            <div className='events__label'>Choix du jour :</div>
                            <LeftCircleFilled className='events__calendar__arrow' onClick={() => changeCurrentDate(dayjs(currentdate).subtract(1, 'day').format('YYYYMMDD'))} />
                            <DatePicker defaultValue={dayjs(currentdate)} value={daypicker} onChange={datepicker_handler} format={'DD/MM/YYYY'} />
                            <RightCircleFilled className='events__calendar__arrow' onClick={() => changeCurrentDate(dayjs(currentdate).add(1, 'day').format('YYYYMMDD'))}/>
                        </div>
                        <Button type="primary" onClick={() => changeCurrentDate(formatdateYYYYMMDD(new Date()))}>Aujourd'hui</Button>
                    </div>
                </div>
                <div className="events__body">
                    <div className="events__body__title">Events</div>
                    <div className="events__body__meetings">
                        {
                            meetings.map((m: MLEventMeeting, imeeting: number) =>
                            {
                                const dates = Array.from(new Set(m.events.map((e: MLEventSaved) => e.context.substring(0, 8))));
                                return (
                                    <div className={'events__body__meeting'} key={'meeting_' + imeeting}>
                                        <div className='events__body__meeting__title'>{m.sic}</div>
                                        {
                                            dates.map((d: string, idate: number) =>
                                            {
                                                return (
                                                    <div key={'date_' + idate}>
                                                        <div className={'events__body__meeting__date'}>{d}</div>
                                                        {
                                                            m.events.filter(e => e.context.substring(0, 8) === d).map((e: MLEventSaved, ievent: number) =>
                                                            {
                                                                const recent: boolean = (now - e.date) < 15000;
                                                                return (
                                                                    <div className={'events__body__meeting__event'} key={'event_' + ievent} onClick={() => showContent(e) }>
                                                                        <div className={'events__body__meeting__event__line' + (recent ? ' events__body__meeting__event__recent' : '')}>
                                                                            <div>{formattime(e.created)}</div>
                                                                            <div>race {e.context.substring(11)}</div>
                                                                        </div>
                                                                    </div>
                                                                )
                                                            })
                                                        }
                                                    </div>
                                                )
                                            })
                                        }
                                    </div>
                                )
                            })
                        }
                    </div>
                </div>
            </div>
            {
                dlgvisible &&
                <Modal width={800} className='events__modal' title={dlgcontent.context} open={dlgvisible} onOk={() => setDlgvisible(false)} onCancel={() => setDlgvisible(false)}
                    styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
                    footer={[<div className='events__modal__footer'><div></div><Button key="cancel" onClick={() => setDlgvisible(false)}>Close</Button></div>]}>
                    {
                        dlgcontent.racebefore &&
                        <div className="events__modal__content">
                            {
                                (() => {
                                    const race1lines = dlgcontent.racebefore ? JSON.stringify(dlgcontent.racebefore, null, '\t').split('\n') : [];
                                    const race2lines = JSON.stringify(dlgcontent.raceafter, null, '\t').split('\n');
                                    const maxLines = Math.max(race1lines.length, race2lines.length);
                                    const dlglines = Array.from({ length: maxLines }, (_, i) => ({a: race1lines[i] ?? '',b: race2lines[i] ?? ''}));
                                    return (
                                        <div>
                                        {
                                            dlglines.map((line, index: number) =>
                                            {
                                                const tabsA = line.a.match(/^\t*/)?.[0].length ?? 0;
                                                const tabsB = line.b.match(/^\t*/)?.[0].length ?? 0;
                                                const diff = line.a !== line.b ? ' events__modal__line__diff' : '';
                                                return (
                                                    <div key={'line_' + index} className={"events__modal__line" + diff}>
                                                        <div className="events__modal__line__a" style={{marginLeft: tabsA * 20}}>{line.a.trimStart()}</div>
                                                        <div className="events__modal__line__b" style={{marginLeft: tabsB * 20}}>{line.b.trimStart()}</div>
                                                    </div>
                                                )
                                            })
                                        }
                                        </div>
                                    );
                                })()
                            }
                        </div>
                    }
                    {
                        !dlgcontent.racebefore &&
                        <div className="events__modal__content">
                            {
                                (() => {
                                    const racelines = dlgcontent.raceafter ? JSON.stringify(dlgcontent.raceafter, null, '\t').split('\n') : [];
                                    return (
                                        <div>
                                        {
                                            racelines.map((line, index: number) =>
                                            {
                                                const tabsA = line.match(/^\t*/)?.[0].length ?? 0;
                                                return (
                                                    <div key={'line_' + index} className={"events__modal__line"}>
                                                        <div className="events__modal__line__full" style={{marginLeft: tabsA * 20}}>{line.trimStart()}</div>
                                                    </div>
                                                )
                                            })
                                        }
                                        </div>
                                    );
                                })()
                            }
                        </div>
                    }
                </Modal>
            }
        </LayoutSite>
    );
}
